import React, { ChangeEvent, useState } from "react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import { Box, Button, Flex, IconButton, Text } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../../public/icons-v1_1";
import useUploadUnoFile from "@/hooks/use-upload-uno-file";
import { Trans } from "react-i18next";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (files: { id: number; name: string }[]) => void;
  isLoading?: boolean;
}

const OrderHistoryOrderInfoOrderModificationModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: Props) => {
  const [files, setFiles] = useState<File[]>([]);
  const { tTitle, tSentence, t } = useI18nHelper();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles((prevFiles) => [...prevFiles, ...Array.from(e.target.files!)]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      setFiles((prevFiles) => [
        ...prevFiles,
        ...Array.from(e.dataTransfer.files),
      ]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileDelete = (fileToDelete: File) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToDelete));
  };

  const { mutateAsync: uploadFile } = useUploadUnoFile();

  const handleConfirm = async () => {
    const uploadedFiles = await Promise.all(
      files.map(async (file) => {
        const response = await uploadFile(file);
        return { id: response.data.id, name: file.name };
      })
    );
    onConfirm(uploadedFiles);
    setFiles([]);
  };

  return (
    <CustomApprovalModal isOpen={isOpen} onClose={onClose}>
      <CustomApprovalModal.Header title={"오더 수정"} onClose={onClose} />
      <CustomApprovalModal.Body>
        <Flex direction="column" gap="16px">
          <Text textStyle="body-md-regular" color="gray.600">
            <Trans
              i18nKey={"order_history:upload.modal.description"}
              components={{
                br: <br />,
              }}
            />
          </Text>
          <Box
            border="1px dashed #D4D4D1"
            borderRadius="6px"
            padding="24px"
            textAlign="center"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <Flex direction="column" align="center" justify="center" gap="8px">
              <Text textStyle="body-md-regular" color="gray.500">
                {tSentence("order_history:upload.modal.drag_msg")}
              </Text>
              <Text textStyle="body-md-regular" color="gray.500">
                {tSentence("order_history:upload.modal.or")}
              </Text>
              <Button
                as="label"
                htmlFor="file-upload"
                variant="outline"
                cursor="pointer"
              >
                {tSentence("order_history:upload.modal.choose_file")}
              </Button>
              <input
                id="file-upload"
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </Flex>
          </Box>
          {files.length > 0 && (
            <Flex direction="column" gap="8px">
              <Flex align="center" gap="4px">
                <Text textStyle="body-sm-bold" color="#444444">
                  {tSentence("order_history:upload.modal.uploaded_file")}
                </Text>
                <Text textStyle="body-sm-bold" color="#1272EF">
                  {files.length}
                </Text>
              </Flex>
              <Flex
                direction="column"
                gap="8px"
                maxHeight="200px"
                overflowY="auto"
              >
                {files.map((file, index) => (
                  <Flex
                    key={index}
                    align="center"
                    justify="space-between"
                    padding="8px 12px"
                    border="1px solid #E2E2E0"
                    borderRadius="6px"
                  >
                    <Flex align="center" gap="8px">
                      <CustomIcon src="file-xls" width={24} height={24} />
                      <Text textStyle="body-sm-regular" color="#444444">
                        {file.name}
                      </Text>
                    </Flex>
                    <Flex align="center" gap="8px">
                      <IconButton
                        aria-label="Delete file"
                        icon={<CustomIcon src="trash" width={16} height={16} />}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleFileDelete(file)}
                      />
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          )}
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        cancelName="취소"
        confirmName="제출"
        onCancel={onClose}
        onConfirm={() => handleConfirm()}
        isLoading={isLoading}
        isConfirmDisabled={files.length === 0 || isLoading}
        confirmBtnBgColor="#1272EF"
      />
    </CustomApprovalModal>
  );
};

export default OrderHistoryOrderInfoOrderModificationModal;
